import { useState } from "react";
import classNames from "classnames";
import { WarningIcon } from "../../../icons/warning.icon";
import { ErrorOutlineIcon } from "../../../icons/error-outline.icon";
import { InfoIcon } from "../../../icons/info.icon";
import { CloseIcon } from "../../../icons/close.icon";
import { TertiaryButtonComponent } from "../buttons/tertiary/tertiary-button.component";
import { IconButtonComponent } from "../buttons/icon-button/icon-button.component";
import { TypographyComponent } from "../typography/typography.component";
import { BannerVariant } from "./banner.constants";
import { CheckmarkIcon } from "../../../icons/checkmark.icon";
import { Portal } from "@mui/material";
import styles from "./banner.styles.module.scss";

export type BannerProps = {
  /** The variant/style of the banner (informative, error, warning, or success). Determines the icon and color scheme. */
  variant: `${BannerVariant}`;
  /** The main content of the banner. */
  message: string;
  /** Label for the CTA button. */
  CTALabel?: string;
  /** Callback fired when the CTA button is clicked. */
  onCTAClick?: () => void;
  /** Callback fired when the close button is clicked. */
  onCloseClick?: () => void;
  /** Additional CSS class to apply to the root element. */
  rootClassName?: string;
  /** Aria-label attribute for the root element. */
  ariaLabel?: string;
  /** Whether to display the close button. */
  showCloseButton?: boolean;
  /** Whether to display the start icon. */
  showStartIcon?: boolean;
  /** Whether to display the CTA button. */
  showCTAButton?: boolean;
  /** Whether to disable the portal behavior. */
  disablePortal?: boolean;
};

const DEFAULT_ICON_BY_VARIANT = {
  [BannerVariant.INFORMATIVE]: <InfoIcon />,
  [BannerVariant.ERROR]: <ErrorOutlineIcon />,
  [BannerVariant.WARNING]: <WarningIcon />,
  [BannerVariant.SUCCESS]: <CheckmarkIcon /> ,
};

export const BannerComponent = ({
  variant,
  message,
  CTALabel,
  onCTAClick = () => {},
  onCloseClick = () => {},
  rootClassName,
  ariaLabel,
  showStartIcon = true,
  showCloseButton = true,
  showCTAButton = false,
  disablePortal = false,
}: BannerProps) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) {
    return null;
  }

  return (
    <Portal disablePortal={disablePortal}>
      <div
        aria-label={ariaLabel}
        className={classNames(
          styles.root,
          rootClassName,
          {
            [styles.bannerInformative]: variant === BannerVariant.INFORMATIVE,
            [styles.bannerError]: variant === BannerVariant.ERROR,
            [styles.bannerWarning]: variant === BannerVariant.WARNING,
            [styles.bannerSuccess]: variant === BannerVariant.SUCCESS,
          }
        )}
      >
        <div className={styles.container}>
          <div className={styles.leftSection}>
            {showStartIcon && DEFAULT_ICON_BY_VARIANT[variant]}
            <TypographyComponent styling="p-default" color="grey9">{message}</TypographyComponent>
          </div>
          <div className={styles.rightSection}>
            {showCTAButton && (
              <TertiaryButtonComponent
                size={"small"}
                onClick={onCTAClick}
                theme={"grey"}
              >
                {CTALabel}
              </TertiaryButtonComponent>
            )}
            {showCloseButton && (
              <IconButtonComponent
                size={"small"}
                theme={"light"}
                onClick={() => {
                  setIsVisible(false);
                  onCloseClick();
                }}
                ariaLabel="close banner"
              >
                <CloseIcon />
              </IconButtonComponent>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};
